import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Plus, ShoppingCart, Trash2, Calendar } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format } from 'date-fns';

// Standard byproduct categories
const STANDARD_BYPRODUCTS = ['Bran', 'Germ', 'Unga Baridi', 'Others'];

export default function OrderEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [packSizes, setPackSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [date, setDate] = useState(new Date());
  const [formData, setFormData] = useState({
    customer_id: '',
    sales_agent_id: '',
    destination: '',
    factory: ''
  });
  const [items, setItems] = useState([]);
  const [byproducts, setByproducts] = useState([]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [orderRes, customersRes, agentsRes, packRes] = await Promise.all([
        api.get(`/orders/${id}`),
        api.get('/customers'),
        api.get('/agents'),
        api.get('/pack-sizes')
      ]);
      
      const orderData = orderRes.data;
      
      // Check if order can be edited (must be Pending)
      if (orderData.dispatch_status && orderData.dispatch_status !== 'Pending') {
        toast.error('Cannot edit dispatched orders. Undo dispatch first.');
        navigate(`/orders/${id}`);
        return;
      }
      
      setOrder(orderData);
      setCustomers(customersRes.data);
      setAgents(agentsRes.data);
      setPackSizes(packRes.data);
      
      // Populate form with existing data
      setDate(new Date(orderData.date));
      setFormData({
        customer_id: orderData.customer_id || '',
        sales_agent_id: orderData.sales_agent_id || '',
        destination: orderData.destination || '',
        factory: orderData.factory || ''
      });
      
      // Populate items
      setItems(orderData.items?.map((item, idx) => ({
        id: item.id || `item-${idx}`,
        pack_size_id: item.pack_size_id,
        pack_size_kg: item.pack_size_kg,
        bales: item.bales,
        price_per_bale: item.price_per_bale
      })) || []);
      
      // Populate byproducts
      setByproducts(orderData.byproducts?.map((bp, idx) => ({
        id: bp.id || `bp-${idx}`,
        product: bp.product,
        kg: bp.kg,
        price_per_kg: bp.price_per_kg
      })) || []);
      
    } catch (error) {
      toast.error('Failed to load order');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, {
      id: Date.now().toString(),
      pack_size_id: '',
      pack_size_kg: 0,
      bales: 0,
      price_per_bale: 0
    }]);
  };

  const updateItem = (itemId, field, value) => {
    setItems(items.map(item => {
      if (item.id !== itemId) return item;
      
      const updated = { ...item, [field]: value };
      
      if (field === 'pack_size_id') {
        const ps = packSizes.find(p => p.id === value);
        updated.pack_size_kg = ps?.size_kg || 0;
      }
      
      if (['bales', 'price_per_bale'].includes(field)) {
        updated[field] = parseFloat(value) || 0;
      }
      
      return updated;
    }));
  };

  const removeItem = (itemId) => {
    setItems(items.filter(i => i.id !== itemId));
  };

  const addByproduct = () => {
    setByproducts([...byproducts, {
      id: Date.now().toString(),
      product: '',
      kg: 0,
      price_per_kg: 0
    }]);
  };

  const updateByproduct = (id, field, value) => {
    setByproducts(byproducts.map(bp =>
      bp.id === id ? { ...bp, [field]: field === 'product' ? value : (parseFloat(value) || 0) } : bp
    ));
  };

  const removeByproduct = (id) => {
    setByproducts(byproducts.filter(bp => bp.id !== id));
  };

  const calculateTotal = () => {
    const itemsTotal = items.reduce((sum, i) => sum + (i.bales * i.price_per_bale), 0);
    const byproductsTotal = byproducts.reduce((sum, bp) => sum + (bp.kg * bp.price_per_kg), 0);
    return itemsTotal + byproductsTotal;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    const validItems = items.filter(i => i.pack_size_id && i.bales > 0 && i.price_per_bale > 0);
    const validByproducts = byproducts.filter(bp => bp.product && bp.kg > 0 && bp.price_per_kg > 0);
    
    if (validItems.length === 0 && validByproducts.length === 0) {
      toast.error('Please add at least one item or byproduct');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/orders/${id}`, {
        date: format(date, 'yyyy-MM-dd'),
        customer_id: formData.customer_id,
        sales_agent_id: formData.sales_agent_id || null,
        destination: formData.destination || null,
        factory: formData.factory || null,
        items: validItems.map(i => ({
          pack_size_id: i.pack_size_id,
          pack_size_kg: i.pack_size_kg,
          bales: i.bales,
          price_per_bale: i.price_per_bale
        })),
        byproducts: validByproducts.map(bp => ({
          product: bp.product,
          kg: bp.kg,
          price_per_kg: bp.price_per_kg
        }))
      });
      toast.success('Order updated successfully');
      navigate(`/orders/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="order-edit-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate(`/orders/${id}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold text-[#1B4D3E]">
            Edit Order: {order?.order_number}
          </h1>
          <p className="text-sm text-slate-500">Update order details (Pending orders only)</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Order Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <DatePickerField date={date} onDateChange={setDate} />
              </div>
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sales Agent (Optional)</Label>
                <Select value={formData.sales_agent_id || "__none__"} onValueChange={(v) => setFormData({ ...formData, sales_agent_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select agent" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Destination</Label>
                <Input
                  value={formData.destination}
                  onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                  placeholder="Delivery location"
                />
              </div>
            </div>
            
            {/* Factory Tag */}
            <div className="space-y-2">
              <Label>Factory Tag (for reporting)</Label>
              <Select value={formData.factory || "__none__"} onValueChange={(v) => setFormData({ ...formData, factory: v === "__none__" ? "" : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select factory (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not specified</SelectItem>
                  <SelectItem value="Ndumberi">Ndumberi</SelectItem>
                  <SelectItem value="Githunguri">Githunguri</SelectItem>
                  <SelectItem value="Both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Order Items */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Order Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </div>
              
              {items.length === 0 ? (
                <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p>No items added</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <div key={item.id} className="p-3 bg-slate-50 rounded-lg space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs">Pack Size *</Label>
                          <Select
                            value={item.pack_size_id}
                            onValueChange={(v) => updateItem(item.id, 'pack_size_id', v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {packSizes.map((ps) => (
                                <SelectItem key={ps.id} value={ps.id}>{ps.size_kg}kg</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Bales *</Label>
                          <Input
                            type="number"
                            value={item.bales || ''}
                            onChange={(e) => updateItem(item.id, 'bales', e.target.value)}
                            placeholder="Qty"
                            min="1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Price/Bale (KES) *</Label>
                          <Input
                            type="number"
                            value={item.price_per_bale || ''}
                            onChange={(e) => updateItem(item.id, 'price_per_bale', e.target.value)}
                            placeholder="Price"
                            min="0"
                          />
                        </div>
                      </div>
                      <div className="flex justify-between items-center pt-1 border-t border-slate-200">
                        <span className="text-sm font-medium">
                          Subtotal: {formatCurrency(item.bales * item.price_per_bale)}
                        </span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(item.id)} className="text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Byproducts */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Byproducts (Optional)</Label>
                <Button type="button" variant="outline" size="sm" onClick={addByproduct}>
                  <Plus className="h-4 w-4 mr-1" /> Add Byproduct
                </Button>
              </div>
              
              {byproducts.map((bp) => (
                <div key={bp.id} className="p-3 bg-amber-50 rounded-lg space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Byproduct *</Label>
                      <Select value={bp.product} onValueChange={(v) => updateByproduct(bp.id, 'product', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {STANDARD_BYPRODUCTS.map((name) => (
                            <SelectItem key={name} value={name}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Quantity (kg) *</Label>
                      <Input
                        type="number"
                        value={bp.kg || ''}
                        onChange={(e) => updateByproduct(bp.id, 'kg', e.target.value)}
                        placeholder="Kg"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Price/kg (KES) *</Label>
                      <Input
                        type="number"
                        value={bp.price_per_kg || ''}
                        onChange={(e) => updateByproduct(bp.id, 'price_per_kg', e.target.value)}
                        placeholder="Price"
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-1 border-t border-amber-200">
                    <span className="text-sm font-medium">
                      Subtotal: {formatCurrency(bp.kg * bp.price_per_kg)}
                    </span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeByproduct(bp.id)} className="text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="pt-4 border-t flex justify-between items-center">
              <span className="text-lg font-semibold">Order Total:</span>
              <span className="text-2xl font-bold text-[#1B4D3E]">{formatCurrency(calculateTotal())}</span>
            </div>

            <div className="flex gap-4">
              <Button type="button" variant="outline" onClick={() => navigate(`/orders/${id}`)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
